import { db } from '../config/firebase.js';
import { collection, getDocs, updateDoc, doc, query, where } from 'firebase/firestore';

export const fixUserData = async () => {
  try {
    console.log('Starting comprehensive user data fix...');
    
    // Get all organisations first
    const orgsRef = collection(db, 'c3_organisations');
    const orgsSnapshot = await getDocs(orgsRef);
    
    console.log(`Found ${orgsSnapshot.docs.length} organisations`);
    
    let totalProcessed = 0;
    let totalUpdated = 0;
    
    // Process each organization
    for (const orgDoc of orgsSnapshot.docs) {
      const orgData = orgDoc.data();
      console.log(`\n--- Processing organization: ${orgData.name || 'Unnamed'} (${orgDoc.id}) ---`);
      
      // Get users for this organization
      const usersRef = collection(db, 'c3_users');
      const usersQuery = query(usersRef, where('orgId', '==', orgDoc.id));
      const usersSnapshot = await getDocs(usersQuery);
      
      console.log(`Found ${usersSnapshot.docs.length} users in this organization`);
      
      // Process each user in this organization
      for (const userDoc of usersSnapshot.docs) {
        const userData = userDoc.data();
        const userId = userDoc.id;
        
        console.log(`\nProcessing user: ${userData.email} (${userId})`);
        console.log(`Current data:`, {
          fullName: userData.fullName || 'NOT_SET',
          firstName: userData.firstName || 'NOT_SET',
          lastName: userData.lastName || 'NOT_SET',
          displayName: userData.displayName || 'NOT_SET',
          lastLogin: userData.lastLogin ? 'HAS_VALUE' : 'NOT_SET',
          lastLoginAt: userData.lastLoginAt ? 'HAS_VALUE' : 'NOT_SET'
        });
        
        const updateData = {};
        
        // Fix fullName field - be more aggressive
        if (!userData.fullName) {
          if (userData.firstName && userData.lastName) {
            updateData.fullName = `${userData.firstName} ${userData.lastName}`;
            console.log(`Setting fullName from firstName + lastName: ${updateData.fullName}`);
          } else if (userData.firstName) {
            updateData.fullName = userData.firstName;
            console.log(`Setting fullName from firstName only: ${updateData.fullName}`);
          } else if (userData.displayName) {
            updateData.fullName = userData.displayName;
            console.log(`Setting fullName from displayName: ${updateData.fullName}`);
          } else if (userData.email) {
            updateData.fullName = userData.email.split('@')[0];
            console.log(`Setting fullName from email: ${updateData.fullName}`);
          }
        }
        
        // Ensure firstName exists if we can extract it
        if (!userData.firstName) {
          if (userData.fullName) {
            const nameParts = userData.fullName.split(' ');
            updateData.firstName = nameParts[0];
            if (nameParts.length > 1) {
              updateData.lastName = nameParts.slice(1).join(' ');
            }
            console.log(`Setting firstName/lastName from fullName: ${updateData.firstName} / ${updateData.lastName || 'none'}`);
          } else if (updateData.fullName) {
            const nameParts = updateData.fullName.split(' ');
            updateData.firstName = nameParts[0];
            if (nameParts.length > 1) {
              updateData.lastName = nameParts.slice(1).join(' ');
            }
            console.log(`Setting firstName/lastName from new fullName: ${updateData.firstName} / ${updateData.lastName || 'none'}`);
          }
        }
        
        // Ensure displayName exists
        if (!userData.displayName && updateData.fullName) {
          updateData.displayName = updateData.fullName;
        } else if (!userData.displayName && userData.fullName) {
          updateData.displayName = userData.fullName;
        }
        
        // DON'T set fake login timestamps - leave them as null if they don't exist
        if (!userData.lastLogin && !userData.lastLoginAt) {
          console.log(`User ${userId} has no login history - leaving as is`);
        }
        
        // Add updatedAt timestamp
        updateData.updatedAt = new Date();
        
        if (Object.keys(updateData).length > 1) { // More than just updatedAt
          console.log(`Updating user ${userId} with:`, updateData);
          try {
            await updateDoc(doc(db, 'c3_users', userId), updateData);
            console.log(`✓ Updated user ${userData.email}`);
            totalUpdated++;
          } catch (error) {
            console.error(`✗ Failed to update user ${userId}:`, error);
          }
        } else {
          console.log(`No updates needed for user ${userData.email}`);
        }
        
        totalProcessed++;
      }
    }
    
    console.log(`\n=== USER DATA FIX COMPLETED ===`);
    console.log(`Total users processed: ${totalProcessed}`);
    console.log(`Total users updated: ${totalUpdated}`);
    
    return { processed: totalProcessed, updated: totalUpdated };
    
  } catch (error) {
    console.error('Error fixing user data:', error);
    throw error;
  }
};

// Function to update login timestamp for current user
export const updateCurrentUserLogin = async (currentUser) => {
  if (!currentUser?.uid) return;
  
  try {
    const userRef = doc(db, 'c3_users', currentUser.uid);
    await updateDoc(userRef, {
      lastLogin: new Date(),
      lastLoginAt: new Date(),
      updatedAt: new Date()
    });
    console.log('Updated login timestamp for user:', currentUser.uid);
  } catch (error) {
    console.error('Error updating login timestamp:', error);
  }
}; 